import React, { useEffect, useRef, useState } from 'react';
import { Dispatch } from 'redux';
import { useSelector, useDispatch } from 'react-redux';
import eurekaMgrs from '@eureka/ui-managers';
import eureka from 'eureka';
import {
  Button,
  ButtonDesign,
  Popover,
  ShellBar,
  StandardListItem,
  List,
  TextAlign,
} from '@ui5/webcomponents-react';
import { PopoverDomRef } from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/copy';
import '@ui5/webcomponents-icons/dist/menu';
import '@ui5/webcomponents-icons/dist/menu2';
import '@ui5/webcomponents-icons/dist/product';
import '@ui5/webcomponents-icons/dist/log.js';
import '@ui5/webcomponents-icons/dist/user-settings.js';
import '@ui5/webcomponents-icons/dist/json-imports/Icons.js';
import { addCustomCSS } from '@ui5/webcomponents-base/dist/Theming';

import { RootState, showMenu, hideMenu } from 'src/common/redux';
import getAvatar from './Avatar';
import LogoutForm from './LogoutForm';
import UserProfile from './UserProfile';
// import Notification from './Notification';
import searchInfo from './searchInfo';
import logo from './logo';
import { MicroFrontendProps, ConfigJson } from 'src/types';
import axios from 'axios';
const { addConfig } = eurekaMgrs.ConfigManager;
const { getFragmentsWithTag, renderFragments, unmountFragments } = eurekaMgrs.FragmentManager;
const { eventBus } = eurekaMgrs;
const { useTranslation } = eureka.I18nProvider;

/* istanbul ignore next */
export const UserProfileFragment = ({
  config,
  history,
}: {
  config: ConfigJson;
  history: History;
}) => {
  const domRef = useRef(null);
  useEffect(() => {
    const container = domRef.current;
    const fragments = getFragmentsWithTag('shell-user_profile', config);
    let renderMap = null;
    if (container) {
      renderMap = renderFragments({
        container,
        fragments,
        renderFragment: (fragmentContainer, fragment) => {
          fragment.render(fragmentContainer, history, {
            config,
            eventBus,
          });
        },
      });
    }
    return () => {
      if (container) {
        unmountFragments({ container, renderMap });
      }
    };
  }, []);
  return (
    <div ref={domRef} className="user-profile-fragment" style={{ width: '100%', height: '100%' }}>
      <UserProfile config={config} eventBus={eventBus} />
    </div>
  );
};

export const handleSidenav = ({
  isMenuShow,
  dispatch,
}: {
  isMenuShow: boolean;
  dispatch: Dispatch;
}) => {
  if (isMenuShow) {
    dispatch(hideMenu());
  } else {
    dispatch(showMenu());
  }
  eventBus.emit('sidenav-show-hide', null, { collapsed: isMenuShow });
  addConfig('SidenavCollapsed', isMenuShow);
};

export const handleSearchInput = (searchInput: HTMLInputElement | null) => {
  const value = searchInput?.value;
  let suggestionItems = [];
  if (value) {
    suggestionItems = searchInfo.searchList.filter(
      (item: { text: string }) => item.text.toUpperCase().indexOf(value.toUpperCase()) === 0,
    );
  }
  // remove the previous suggestions
  [].slice.call(searchInput?.children).forEach((child) => {
    searchInput?.removeChild(child);
  });
  // add the new suggestions according to the  user input
  suggestionItems.forEach((item: { text: string }) => {
    const li = document.createElement('ui5-suggestion-item');
    // @ts-ignore
    li.text = item.text;
    searchInput?.appendChild(li);
  });
};

export const handleNotificationClick = ({
  event,
  notificationPopoverRef,
  profileMenuRef,
}: {
  event;
  notificationPopoverRef: React.RefObject<PopoverDomRef>;
  profileMenuRef: React.RefObject<PopoverDomRef>;
}) => {
  closeProfileMenu(profileMenuRef);
  if (notificationPopoverRef && notificationPopoverRef.current) {
    notificationPopoverRef.current.showAt(event.detail.targetRef);
  }
};

export const handleProfileClick = ({
  event,
  notificationPopoverRef,
  profileMenuRef,
}: {
  event;
  notificationPopoverRef: React.RefObject<PopoverDomRef>;
  profileMenuRef: React.RefObject<PopoverDomRef>;
}) => {
  notificationPopoverRef?.current?.close();
  profileMenuRef?.current?.showAt(event.detail.targetRef);
};

export const handleProductSwitchClick = ({ event, productSwitchDomRef }) => {
  console.log('event.detail.targetRef', event.detail.targetRef);
  productSwitchDomRef?.current?.showAt(event.detail.targetRef);
};

const openProfileSetting = (event: React.MouseEvent, config: ConfigJson, t: any) => {
  closeProfileMenu();
  eventBus.emit('dialog-show-hide', '', {
    type: 'user-profile',
    data: {
      showDialog: true,
      title: t('Header_ProfileSettings', 'Settings', { type: 'tit', desc: '' }),
      content: () => {
        return <UserProfile config={config} eventBus={eventBus} />;
      },
    },
  });
};

const closeProfileMenu = (profileMenuRef?: React.RefObject<PopoverDomRef>) => {
  profileMenuRef?.current?.close && profileMenuRef.current.close();
};

const logout = () => {
  closeProfileMenu();
  eventBus.emit('dialog-show-hide', '', {
    type: 'user-logout',
    data: {
      showDialog: true,
      content: <LogoutForm eventBus={eventBus} />,
    },
  });
};

type Props = Pick<MicroFrontendProps, 'config' | 'settings' | 'user' | 'history'>;
export const Header: React.FC<Props> = ({ config, user, settings, history }) => {
  const notificationPopoverRef = useRef<PopoverDomRef>(null);
  const profileMenuRef = useRef<PopoverDomRef>(null);
  const productSwitchDomRef = useRef<PopoverDomRef>(null);
  // const searchInputRef = useRef(null);
  const isMenuShow = useSelector((state: RootState) => state.common.isMenuShow);
  const dispatch = useDispatch();
  const shellbarRef = useRef(null);
  const { t } = useTranslation();

  const [projectList, setProjectionList] = useState([{ alisa: 'loading', isAdmin: false }]);
  const [project, setProject] = useState(window.localStorage.getItem('project'));
  const [loading, setLoading] = useState(true);

  addCustomCSS(
    'ui5-shellbar-devx-shell',
    `.ui5-shellbar-menu-button-title, .ui5-shellbar-title {
      font-size: 14px;
      font-weight: 400;
    }
    `,
  );

  addCustomCSS(
    'ui5-shellbar-devx-shell',
    `.ui5-shellbar-menu-button.ui5-shellbar-menu-button--interactive {
      padding-bottom: 7px; 
    }
    `,
  );

  useEffect(() => {
    addConfig('SidenavCollapsed', !isMenuShow);
  }, [isMenuShow]);

  useEffect(() => {
    eventBus.on('wa-loaded', () => {
      addConfig('waLoaded', true);
    });
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      console.log('ShellBar', ShellBar);
      if (shellbarRef.current) {
        clearInterval(interval);
        const newElement = document.createElement('img');
        newElement.src =
          'data:image/png;base64,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';
        newElement.alt = 'SAP Logo';
        newElement.slot = 'logo';
        newElement.style = 'padding-bottom: 3.5px';
        let imgCount = 0;
        Array.from(shellbarRef.current.children[0].children).forEach((element) => {
          console.log('element', element.tagName);
          if (element.tagName === 'IMG') {
            imgCount++;
          }
        });
        if (imgCount === 1) {
          shellbarRef.current.children[0].insertBefore(newElement, shellbarRef.current.nextSibling);
        }
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (window.localStorage.getItem('projectList')) {
      setLoading(false);
      setProjectionList(JSON.parse(window.localStorage.getItem('projectList')));
    }

    if (project == undefined || project == 'undefined') {
      if (JSON.parse(window.localStorage.getItem('projectList'))?.length) {
        setProject(JSON.parse(window.localStorage.getItem('projectList'))[0].alias);
      }
    }

    // axios.get('/api/dev-info/v1/user').then((data) => {
    //   if (
    //     data.data?.organizations?.length !==
    //     JSON.parse(window.localStorage.getItem('projectList'))?.length
    //   ) {
    //     setLoading(false);
    //     setProjectionList(data.data?.organizations);
    //   }
    // });
  }, []);

  return (
    <div
      ref={shellbarRef}
      data-testid="rpp.header"
      style={{
        background: `var(--sapShellColor)`,
      }}
    >
      <ShellBar
        data-testid={'rpp.header.shellBar'}
        id="eureka-shellbar"
        className="shellbar"
        logo={<img alt="SAP Logo" src={logo} />}
        menuItems={
          <>
            {projectList &&
              projectList.map((project, idx) => {
                return (
                  <StandardListItem data-key={project.alias} key={idx}>
                    {project.alias}
                  </StandardListItem>
                );
              })}
          </>
        }
        onMenuItemClick={(event) => {
          let newUrl = '/devx/';
          window.location.pathname.split('/').forEach((item, index) => {
            if (index === 2) {
              newUrl = newUrl + event?.detail?.item?.innerText;
            } else if (index > 2) {
              newUrl += '/' + item;
            }
          });
          console.log('newUrl', newUrl);
          history.push(newUrl);
          location.reload();
        }}
        primaryTitle={`project: ${project}`}
        profile={getAvatar(user)}
        showProductSwitch
        onProductSwitchClick={(event) => {
          handleProductSwitchClick({ event, productSwitchDomRef });
          console.log('onProductSwitchClick');
        }}
        startButton={
          <Button
            data-testid={'shellbarHamburgerButton'}
            className="shellbar-hamburger-button"
            icon={isMenuShow ? 'menu' : 'menu2'}
            design={ButtonDesign.Transparent}
            onClick={() => handleSidenav({ isMenuShow, dispatch })}
            data-ui5-stable="menu-toggle-button"
          />
        }
        onProfileClick={(event) =>
          handleProfileClick({ event, notificationPopoverRef, profileMenuRef })
        }
        // showNotifications
        // notificationsCount={notifications?.length > 99 ? '99+' : notifications?.length}
        // onNotificationsClick={(event) =>
        //   handleNotificationClick({ event, notificationPopoverRef, profileMenuRef })
        // }
        // searchField={
        //   <Input
        //     data-testid={'rpp.header.searchInput'}
        //     data-ui5-stable="search-input"
        //     ref={searchInputRef}
        //     type="Text"
        //     value={undefined}
        //     valueState="None"
        //     placeholder="Enter a term"
        //     showSuggestions
        //     onInput={() => handleSearchInput(searchInputRef.current)}
        //     // onSuggestionItemSelect={null}
        //   />
        // }
      ></ShellBar>

      {/* <Popover
        ref={productSwitchDomRef}
        allowTargetOverlap
        horizontalAlign="Right"
        placementType="Bottom"
        preventFocusRestore={false}
      >
        <List>
          <StandardListItem
            type="Active"
            selected={false}
            onClick={(evt) => openProfileSetting(evt, config, t)}
            style={{ margin: '3px' }}
            data-key="1"
            // infoState="None"
          >
            ede
          </StandardListItem>
          <StandardListItem
            type="Active"
            selected={false}
            onClick={logout}
            style={{ margin: '2px' }}
            data-key="2"
            // infoState="None"
          >
            ededev
          </StandardListItem>
        </List>
      </Popover> */}

      {/* <Popover
        ref={notificationPopoverRef}
        allowTargetOverlap
        horizontalAlign="Right"
        placementType="Bottom"
        preventFocusRestore={false}
      >
        <Notification
          settings={settings}
          allNotifications={notifications}
          onDismissNotification={actions.dismissNotification}
          onDismissAllNotifications={actions.dismissAllNotifications}
        />
      </Popover> */}

      <Popover
        data-testid={'rpp.header.popover'}
        ref={profileMenuRef}
        allowTargetOverlap
        horizontalAlign="Right"
        placementType="Bottom"
        preventFocusRestore={false}
      >
        <List>
          <StandardListItem
            type="Active"
            icon={'user-settings'}
            selected={false}
            onClick={(evt) => openProfileSetting(evt, config, t)}
            style={{ margin: '3px' }}
            data-key="1"
            // infoState="None"
          >
            {t('ProfileSettings', 'Settings', { type: 'but', desc: '' })}
          </StandardListItem>
          <StandardListItem
            type="Active"
            icon="log"
            selected={false}
            onClick={logout}
            style={{ margin: '2px' }}
            data-key="2"
            // infoState="None"
          >
            {t('Header_Logout')}
          </StandardListItem>
        </List>
      </Popover>
    </div>
  );
};

export default Header;
